import { ChevronLeft, ChevronRight, Download, X } from "lucide-react";
import { type ReactNode, type TouchEvent, useEffect, useRef, useState } from "react";
import { AiReviewPanel } from "@/components/ai/AiReviewPanel";
import { PhotoChecklist } from "@/components/checklist/PhotoChecklist";
import { ExtractionPanel } from "@/components/photos/ExtractionPanel";
import { PhotoFileFace } from "@/components/photos/PhotoFileFace";
import { PhotoShareState } from "@/components/sharing/PhotoShareState";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { companyLabel, formatCapturedAt, formatGps } from "@/lib/format";
import { checkSessionAfterImageError, photoFileUrl } from "@/lib/photo-files";
import type { Album, Photo } from "@/schemas/photos";

const NOT_RECORDED = "Not recorded";
const SWIPE_PX = 50;
/** Which neighbors to have ready, nearest first. Reading forward is the common case. */
const PRELOAD_OFFSETS = [1, -1, 2];
/** Ask for the next page this close to the end of what is loaded. */
const LOAD_AHEAD = 5;

function MetaRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 text-sm break-words">{children}</dd>
    </div>
  );
}

/** Keeps the neighbors decoded, so the next photo is on screen as soon as it is asked for. */
function usePreloadedNeighbors(photos: Photo[], index: number) {
  const loaded = useRef(new Map<string, HTMLImageElement>());
  useEffect(() => {
    const wanted = new Set<string>();
    for (const offset of PRELOAD_OFFSETS) {
      const photo = photos[index + offset];
      if (photo?.kind !== "image") continue;
      wanted.add(photo.id);
      if (loaded.current.has(photo.id)) continue;
      const image = new Image();
      image.src = photoFileUrl(photo.id, "display");
      image.decode().catch(() => undefined);
      loaded.current.set(photo.id, image);
    }
    for (const id of loaded.current.keys()) {
      if (!wanted.has(id)) loaded.current.delete(id);
    }
  }, [photos, index]);
}

interface PhotoViewerProps {
  photos: Photo[];
  total: number;
  albums: Album[];
  index: number;
  hasNext: boolean;
  onIndexChange: (index: number) => void;
  onLoadMore: () => void;
  onClose: () => void;
  /** Set for someone who manages sharing: the project the photo's shares belong to. */
  sharingProjectId?: string;
}

export function PhotoViewer({
  photos,
  total,
  albums,
  index,
  hasNext,
  onIndexChange,
  onLoadMore,
  onClose,
  sharingProjectId,
}: PhotoViewerProps) {
  const photo = photos[index];
  const touchStart = useRef<number | null>(null);
  const [failedId, setFailedId] = useState<string | null>(null);
  usePreloadedNeighbors(photos, index);

  useEffect(() => {
    if (hasNext && index >= photos.length - LOAD_AHEAD) onLoadMore();
  }, [hasNext, index, photos.length, onLoadMore]);

  if (!photo) return null;

  const canGoBack = index > 0;
  const canGoForward = index < photos.length - 1;
  const goBack = () => canGoBack && onIndexChange(index - 1);
  const goForward = () => canGoForward && onIndexChange(index + 1);

  function onKeyDown(event: React.KeyboardEvent) {
    if (event.key === "ArrowLeft") goBack();
    if (event.key === "ArrowRight") goForward();
  }

  function onTouchEnd(event: TouchEvent) {
    const start = touchStart.current;
    const end = event.changedTouches[0]?.clientX;
    touchStart.current = null;
    if (start === null || end === undefined) return;
    if (start - end > SWIPE_PX) goForward();
    if (end - start > SWIPE_PX) goBack();
  }

  const failed = failedId === photo.id;
  const album = albums.find((a) => a.id === photo.albumId);
  const gps = formatGps(photo.lat, photo.lng);
  const position = `${(index + 1).toLocaleString("en-US")} of ${total.toLocaleString("en-US")}`;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        onKeyDown={onKeyDown}
        className="top-0 left-0 flex h-dvh w-screen max-w-none translate-x-0 translate-y-0 flex-col gap-0 rounded-none border-0 p-0 md:flex-row [&>button:last-child]:hidden"
      >
        <DialogTitle className="sr-only">{photo.fileName}</DialogTitle>
        <DialogDescription className="sr-only">
          Photo {position}. Use the left and right arrow keys to move between photos.
        </DialogDescription>

        <div
          data-testid="viewer-stage"
          className="relative h-[60dvh] shrink-0 bg-black text-white md:h-auto md:min-w-0 md:flex-1"
          onTouchStart={(event) => {
            touchStart.current = event.touches[0]?.clientX ?? null;
          }}
          onTouchEnd={onTouchEnd}
        >
          {photo.kind === "image" && !failed ? (
            <>
              <img
                key={`thumb-${photo.id}`}
                src={photoFileUrl(photo.id, "thumb")}
                alt=""
                className="absolute inset-0 size-full object-contain"
              />
              <img
                key={photo.id}
                data-testid="viewer-image"
                src={photoFileUrl(photo.id, "display")}
                alt={photo.fileName}
                onError={() => {
                  setFailedId(photo.id);
                  checkSessionAfterImageError();
                }}
                className="absolute inset-0 size-full object-contain"
              />
            </>
          ) : (
            <PhotoFileFace photo={photo} failed={failed} />
          )}

          <span
            data-testid="viewer-position"
            className="absolute top-3 left-3 rounded-full bg-black/60 px-3 py-1 text-sm font-medium tabular-nums"
          >
            {position}
          </span>
          <DialogClose asChild>
            <Button
              variant="secondary"
              size="icon"
              aria-label="Close viewer"
              className="absolute top-3 right-3 rounded-full"
            >
              <X className="size-4" />
            </Button>
          </DialogClose>
          {canGoBack && (
            <Button
              variant="secondary"
              size="icon"
              aria-label="Previous photo"
              onClick={goBack}
              className="absolute top-1/2 left-3 -translate-y-1/2 rounded-full"
            >
              <ChevronLeft className="size-5" />
            </Button>
          )}
          {canGoForward && (
            <Button
              variant="secondary"
              size="icon"
              aria-label="Next photo"
              onClick={goForward}
              className="absolute top-1/2 right-3 -translate-y-1/2 rounded-full"
            >
              <ChevronRight className="size-5" />
            </Button>
          )}
        </div>

        <aside className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4 md:w-80 md:flex-none">
          <dl data-testid="viewer-details" className="space-y-3">
            <MetaRow label="File name">{photo.fileName}</MetaRow>
            <MetaRow label="Album">{album?.name ?? NOT_RECORDED}</MetaRow>
            <MetaRow label={photo.capturedSource === "upload" ? "Uploaded" : "Captured"}>
              {formatCapturedAt(photo.capturedAt)}
              {photo.capturedSource === "upload" && (
                <span className="block text-xs text-muted-foreground">
                  The file has no capture time, so this is its upload time.
                </span>
              )}
            </MetaRow>
            {photo.uploadedVia && (
              <MetaRow label="Source">
                {photo.uploadedVia === "field"
                  ? "Uploaded from the field app"
                  : "Uploaded from the web"}
              </MetaRow>
            )}
            <MetaRow label="GPS">
              {gps
                ? `${gps}${photo.gpsAccuracyM === null ? "" : ` (±${Math.round(photo.gpsAccuracyM)} m)`}`
                : NOT_RECORDED}
            </MetaRow>
            <MetaRow label="Company">
              {photo.company ? companyLabel(photo.company) : NOT_RECORDED}
            </MetaRow>
            <MetaRow label="Device">{photo.device ?? NOT_RECORDED}</MetaRow>
          </dl>
          {photo.kind === "image" && <ExtractionPanel key={photo.id} photo={photo} />}
          {photo.kind === "image" && (
            <AiReviewPanel key={`ai-${photo.id}`} photo={photo} projectId={sharingProjectId} />
          )}
          {sharingProjectId && <PhotoShareState photo={photo} projectId={sharingProjectId} />}
          {sharingProjectId && album?.isMc && (
            <PhotoChecklist photo={photo} projectId={sharingProjectId} />
          )}
          {photo.kind !== "missing" && (
            <Button asChild variant="outline" className="w-full">
              <a href={photoFileUrl(photo.id, "original")} download>
                <Download className="size-4" />
                Download original
              </a>
            </Button>
          )}
        </aside>
      </DialogContent>
    </Dialog>
  );
}
